/**
 * Blog Search with Shopify Backend Search API
 * Searches articles across all blogs using Shopify's search endpoint
 */

class BlogSearch {
  constructor(options = {}) {
    this.searchInput = document.getElementById(options.inputId || 'slikkepott-blog-search');
    this.resultsContainer = document.getElementById(options.resultsContainerId || 'slikkepott-search-results');
    this.articlesContainer = document.getElementById(options.articlesContainerId || 'slikkepott-articles-container');
    this.paginationContainer = document.getElementById(options.paginationId || 'slikkepott-pagination');
    this.resultsCountEl = document.getElementById(options.resultsCountId || 'slikkepott-results-count');
    this.loadingEl = document.getElementById(options.loadingId || 'slikkepott-search-loading');
    
    this.searchTimeout = null;
    this.debounceDelay = options.debounceDelay || 300;
    this.minSearchLength = options.minSearchLength || 2;
    this.isSearching = false;
    
    this.init();
  }
  
  init() {
    if (!this.searchInput) {
      console.warn('Blog search input not found');
      return;
    }
    
    this.searchInput.addEventListener('input', this.handleInput.bind(this));
    this.searchInput.addEventListener('keydown', this.handleKeydown.bind(this));
    
    // Check URL for search query on page load
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    if (query) {
      this.searchInput.value = query;
      this.performSearch(query);
    }
  }
  
  handleInput(e) {
    const query = e.target.value.trim();
    
    clearTimeout(this.searchTimeout);
    
    if (query.length === 0) {
      this.resetSearch();
      return;
    }
    
    if (query.length < this.minSearchLength) {
      this.showMessage(`Skriv minst ${this.minSearchLength} tegn for å søke...`);
      return;
    }
    
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceDelay);
  }
  
  handleKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      clearTimeout(this.searchTimeout);
      const query = this.searchInput.value.trim();
      if (query.length >= this.minSearchLength) {
        this.performSearch(query);
      }
    }
    
    if (e.key === 'Escape') {
      this.searchInput.value = '';
      this.resetSearch();
    }
  }
  
  async performSearch(query) {
    if (this.isSearching) return;
    
    this.isSearching = true;
    this.showLoading(true);
    this.hideOriginalArticles();
    
    try {
      // Use Shopify's search endpoint with article type filter
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=article&resources[limit]=50`;
      
      const response = await fetch(searchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      
      const data = await response.json();
      const articles = data.resources?.results?.articles || [];
      
      this.renderResults(articles, query);
      
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to standard search page scraping
      await this.fallbackSearch(query);
    } finally {
      this.isSearching = false;
      this.showLoading(false);
    }
  }
  
  async fallbackSearch(query) {
    try {
      // Fallback: fetch the search page and parse results
      const searchUrl = `/search?q=${encodeURIComponent(query)}&type=article&view=json`;
      
      const response = await fetch(searchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Fallback search failed: ${response.status}`);
      }
      
      const html = await response.text();
      
      // Try to parse JSON if the view=json returns JSON
      try {
        const data = JSON.parse(html);
        if (data.articles) {
          this.renderResults(data.articles, query);
          return;
        }
      } catch (e) {
        // Not JSON, parse HTML
      }
      
      // Parse HTML to extract articles
      const articles = this.parseSearchHTML(html);
      this.renderResults(articles, query);
      
    } catch (error) {
      console.error('Fallback search error:', error);
      this.showMessage('Søket feilet. Vennligst prøv igjen.');
    }
  }
  
  parseSearchHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const articles = [];
    
    // Try to find article results in common patterns
    const articleElements = doc.querySelectorAll('[data-type="article"], .article-card, .search-item--article');
    
    articleElements.forEach(el => {
      const titleEl = el.querySelector('h2, h3, .title, .article-title');
      const linkEl = el.querySelector('a[href*="/blogs/"]');
      const imageEl = el.querySelector('img');
      const excerptEl = el.querySelector('p, .excerpt, .description');
      
      if (titleEl && linkEl) {
        articles.push({
          title: titleEl.textContent.trim(),
          url: linkEl.getAttribute('href'),
          image: imageEl ? imageEl.getAttribute('src') : null,
          excerpt: excerptEl ? excerptEl.textContent.trim() : ''
        });
      }
    });
    
    return articles;
  }
  
  renderResults(articles, query) {
    if (!this.resultsContainer) return;
    
    // Clear previous results
    this.resultsContainer.innerHTML = '';
    
    // Update results count
    if (this.resultsCountEl) {
      if (articles.length === 0) {
        this.resultsCountEl.textContent = `Ingen oppskrifter funnet for "${query}"`;
      } else if (articles.length === 1) {
        this.resultsCountEl.textContent = `1 oppskrift funnet for "${query}"`;
      } else {
        this.resultsCountEl.textContent = `${articles.length} oppskrifter funnet for "${query}"`;
      }
    }
    
    if (articles.length === 0) {
      this.resultsContainer.innerHTML = `
        <div class="slikkepott-no-results">
          <p>Ingen oppskrifter funnet for "<strong>${this.escapeHtml(query)}</strong>"</p>
          <p>Prøv å søke med andre ord eller sjekk stavemåten.</p>
        </div>
      `;
      return;
    }
    
    // Render article cards
    const html = articles.map(article => this.renderArticleCard(article)).join('');
    this.resultsContainer.innerHTML = html;
    
    // Show results container
    this.resultsContainer.style.display = 'grid';
  }
  
  renderArticleCard(article) {
    const imageUrl = article.image || article.featured_image?.url || null;
    const title = article.title || 'Uten tittel';
    const url = article.url || '#';
    const excerpt = article.body ? this.stripHtml(article.body).substring(0, 150) + '...' : 
                    (article.excerpt || '');
    
    // Check if we're on the blogger page (uses different class names)
    const isBloggerPage = document.querySelector('.slikkepott-tagged-grid') !== null;
    
    if (isBloggerPage) {
      return `
        <div class="slikkepott-tagged-card slikkepott-search-result" style="display: flex;">
          <a href="${url}" class="slikkepott-article-image-link">
            ${imageUrl ? `
              <img src="${this.getImageUrl(imageUrl, '500x400')}" 
                   alt="${this.escapeHtml(title)}" 
                   class="slikkepott-tagged-image"
                   loading="lazy">
            ` : `
              <div class="slikkepott-image-placeholder">🥐</div>
            `}
          </a>
          
          <div class="slikkepott-tagged-content">
            <h2 class="slikkepott-tagged-article-title">${this.escapeHtml(title)}</h2>
            
            ${excerpt ? `
              <p class="slikkepott-tagged-excerpt">${this.escapeHtml(excerpt)}</p>
            ` : ''}
            
            <a href="${url}" class="slikkepott-tagged-button">Les mer</a>
          </div>
        </div>
      `;
    }
    
    return `
      <div class="slikkepott-article-card slikkepott-search-result">
        <a href="${url}" class="slikkepott-article-image-link">
          ${imageUrl ? `
            <img src="${this.getImageUrl(imageUrl, '500x400')}" 
                 alt="${this.escapeHtml(title)}" 
                 class="slikkepott-article-image"
                 loading="lazy">
          ` : `
            <div class="slikkepott-image-placeholder">🥐</div>
          `}
        </a>
        
        <div class="slikkepott-article-content">
          <h2 class="slikkepott-article-title">${this.escapeHtml(title)}</h2>
          
          ${excerpt ? `
            <p class="slikkepott-article-excerpt">${this.escapeHtml(excerpt)}</p>
          ` : ''}
          
          <a href="${url}" class="slikkepott-read-more-button">Les mer</a>
        </div>
      </div>
    `;
  }
  
  resetSearch() {
    // Clear results
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
      this.resultsContainer.style.display = 'none';
    }
    
    // Clear results count
    if (this.resultsCountEl) {
      this.resultsCountEl.textContent = '';
    }
    
    // Show original articles and pagination
    this.showOriginalArticles();
    
    // Clear URL parameter
    const url = new URL(window.location);
    url.searchParams.delete('q');
    window.history.replaceState({}, '', url);
  }
  
  hideOriginalArticles() {
    if (this.articlesContainer) {
      this.articlesContainer.style.display = 'none';
    }
    if (this.paginationContainer) {
      this.paginationContainer.style.display = 'none';
    }
    
    // Also hide radio inputs for CSS pagination
    document.querySelectorAll('.slikkepott-pagination-radio').forEach(radio => {
      radio.style.display = 'none';
    });
    
    // Hide tag filter buttons when searching
    const tagFilterWrapper = document.getElementById('slikkepott-filter-container');
    if (tagFilterWrapper) {
      tagFilterWrapper.style.display = 'none';
    }
  }
  
  showOriginalArticles() {
    if (this.articlesContainer) {
      this.articlesContainer.style.display = '';
    }
    if (this.paginationContainer) {
      this.paginationContainer.style.display = '';
    }
    
    // Show radio inputs
    document.querySelectorAll('.slikkepott-pagination-radio').forEach(radio => {
      radio.style.display = '';
    });
    
    // Show tag filter buttons
    const tagFilterWrapper = document.getElementById('slikkepott-filter-container');
    if (tagFilterWrapper) {
      tagFilterWrapper.style.display = '';
    }
  }
  
  showLoading(show) {
    if (this.loadingEl) {
      this.loadingEl.style.display = show ? 'flex' : 'none';
    }
    
    if (show && this.resultsContainer) {
      this.resultsContainer.innerHTML = `
        <div class="slikkepott-search-loading-inline">
          <div class="slikkepott-spinner"></div>
          <p>Søker...</p>
        </div>
      `;
      this.resultsContainer.style.display = 'block';
    }
  }
  
  showMessage(message) {
    if (this.resultsCountEl) {
      this.resultsCountEl.textContent = message;
    }
  }
  
  // Utility functions
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }
  
  getImageUrl(url, size) {
    if (!url) return '';
    
    // Handle Shopify CDN URLs
    if (url.includes('cdn.shopify.com')) {
      // Replace size in existing URL or add it
      const sizePattern = /_\d+x\d+/;
      if (sizePattern.test(url)) {
        return url.replace(sizePattern, `_${size}`);
      }
      // Add size before file extension
      const extMatch = url.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
      if (extMatch) {
        return url.replace(extMatch[0], `_${size}${extMatch[0]}`);
      }
    }
    
    return url;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Check for blog-filtering.liquid section (new section with backend search)
  if (document.getElementById('slikkepott-blog-search')) {
    window.blogSearch = new BlogSearch({
      inputId: 'slikkepott-blog-search',
      resultsContainerId: 'slikkepott-search-results',
      articlesContainerId: 'slikkepott-articles-container',
      paginationId: 'slikkepott-pagination',
      resultsCountId: 'slikkepott-results-count',
      loadingId: 'slikkepott-search-loading'
    });
  }
  
  // Check for page.blogger.json template (alternative element IDs)
  if (document.getElementById('slikkepott-search') && !document.getElementById('slikkepott-blog-search')) {
    window.blogSearch = new BlogSearch({
      inputId: 'slikkepott-search',
      resultsContainerId: 'slikkepott-backend-search-results', // Will be created dynamically
      articlesContainerId: null, // Uses .slikkepott-tagged-grid
      paginationId: 'slikkepott-pagination',
      resultsCountId: 'slikkepott-results-count',
      loadingId: null
    });
    
    // Create results container for blogger page if it doesn't exist
    const searchContainer = document.querySelector('.slikkepott-search-container');
    if (searchContainer && !document.getElementById('slikkepott-backend-search-results')) {
      const resultsDiv = document.createElement('div');
      resultsDiv.id = 'slikkepott-backend-search-results';
      resultsDiv.className = 'slikkepott-tagged-grid';
      resultsDiv.style.display = 'none';
      
      // Insert after the results count element
      const resultsCount = document.getElementById('slikkepott-results-count');
      if (resultsCount && resultsCount.parentNode) {
        resultsCount.parentNode.insertBefore(resultsDiv, resultsCount.nextSibling);
      }
      
      // Update the BlogSearch instance with the new container
      if (window.blogSearch) {
        window.blogSearch.resultsContainer = resultsDiv;
        window.blogSearch.articlesContainer = document.querySelector('.slikkepott-tagged-grid');
      }
    }
  }
});
